import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, Building2, Copy, KeyRound, Landmark, Lock, Receipt, RefreshCw, Send } from 'lucide-react';
import { fmtDate, naira } from '@/lib/format';
import { getWallet, getWalletTransactions, getWalletTransfers, walletError } from '@/api/parent';
import { OnboardingForm } from '../wallet/OnboardingForm';
import { LinkExisting } from '../wallet/LinkExisting';
import { SendDialog } from '../wallet/SendDialog';
import { PinManager } from '../wallet/ui';
import { PageTitle } from '../ui';

const STATUS: Record<string, string> = { successful: 'text-green-700', success: 'text-green-700', processing: 'text-amber-700', pending: 'text-amber-700', failed: 'text-red-600' };

export function WalletPage() {
  const qc = useQueryClient();
  const [send, setSend] = useState<'bank' | 'wallet' | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<'all' | 'transfers'>('all');
  const [openNew, setOpenNew] = useState(false);

  const wallet = useQuery({ queryKey: ['parent-wallet'], queryFn: () => getWallet() });
  const w = wallet.data;
  const ready = !!w && w.onboarded && !w.requires_bvn;
  const txns = useQuery({ queryKey: ['parent-wallet-transactions'], queryFn: () => getWalletTransactions(), enabled: ready });
  const transfers = useQuery({ queryKey: ['parent-wallet-transfers'], queryFn: () => getWalletTransfers(), enabled: ready && tab === 'transfers' });

  const reload = () => qc.invalidateQueries({ predicate: (q) => ['parent-wallet', 'parent-home', 'parent-fees'].some((k) => String(q.queryKey[0]).startsWith(k)) });
  const refresh = async () => {
    setRefreshing(true);
    try { qc.setQueryData(['parent-wallet'], await getWallet(true)); await txns.refetch(); } finally { setRefreshing(false); }
  };
  const copy = async (text: string) => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ } };

  if (wallet.isLoading) return <p className="text-sm text-slate-500">Loading wallet…</p>;
  if (wallet.isError || !w) {
    return (
      <div className="space-y-4">
        <PageTitle kicker="Payments" title="Wallet" />
        <div className="bg-white rounded-2xl border border-slate-200 p-5"><p className="text-sm text-red-600 mb-3">{walletError(wallet.error).message}</p>
          <button onClick={() => wallet.refetch()} className="px-4 py-2 rounded-xl bg-[#0A2540] text-white text-sm font-semibold">Try again</button></div>
      </div>
    );
  }

  const fa = w.funding_account;
  const balance = Number(w.balance ?? 0);
  const needPin = () => setPinOpen(true);
  const action = (label: string, Icon: any, onClick: () => void, to?: string) => {
    const cls = 'flex flex-col items-center gap-1.5 rounded-2xl bg-white/10 hover:bg-white/20 py-3 text-xs font-semibold';
    return to ? <Link to={to} className={cls}><Icon className="w-5 h-5" />{label}</Link> : <button onClick={onClick} className={cls}><Icon className="w-5 h-5" />{label}</button>;
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <PageTitle kicker="Payments" title="Wallet" sub="Your own account number for school payments. Every payment and transfer needs your 4-digit PIN." />
      {notice && <p className="text-sm bg-green-50 text-green-800 rounded-xl px-3 py-2 flex justify-between gap-3"><span>{notice}</span><button onClick={() => setNotice('')}>✕</button></p>}

      {!ready && w.existing_wallet && !openNew && <LinkExisting w={w} onDone={(m) => { reload(); setNotice(m); }} onNew={() => setOpenNew(true)} />}
      {!ready && (!w.existing_wallet || openNew) && <OnboardingForm onDone={() => { reload(); setNotice('Your wallet is open. Set your PIN to start paying.'); }} />}

      {ready && (
        <>
          <section className="rounded-3xl bg-gradient-to-br from-[#0A2540] to-[#12406b] text-white p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-white/60 font-bold">Available balance</p>
                <p className="text-3xl sm:text-4xl font-extrabold mt-1">{naira(balance)}</p>
              </div>
              <button onClick={refresh} disabled={refreshing} className="p-2 rounded-xl bg-white/10 hover:bg-white/20" aria-label="Refresh balance"><RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /></button>
            </div>
            {fa?.ready && fa.account_number ? (
              <div className="mt-4 rounded-2xl bg-white/10 p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] text-white/60">Top up: transfer from any bank to</p>
                  <p className="font-mono text-xl font-bold tracking-wider">{fa.account_number}</p>
                  <p className="text-xs text-white/70 truncate">{fa.account_name} · {fa.bank_name}</p>
                </div>
                <button onClick={() => copy(fa.account_number!)} className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white text-[#0A2540] text-xs font-bold"><Copy className="w-3.5 h-3.5" />{copied ? 'Copied' : 'Copy'}</button>
              </div>
            ) : <p className="mt-4 text-sm text-white/70">Your account number is being prepared. Tap refresh in a moment.</p>}
            {w.shared && <p className="mt-3 text-xs text-white/70 inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />One wallet for all your children’s schools · opened at {w.opened_at_school}</p>}

            <div className="grid grid-cols-4 gap-2 mt-4">
              {action('Pay fees', Receipt, () => undefined, '/parent/fees')}
              {action('To bank', Landmark, () => (w.has_pin ? setSend('bank') : needPin()))}
              {action('To wallet', Send, () => (w.has_pin ? setSend('wallet') : needPin()))}
              {action(w.has_pin ? 'Change PIN' : 'Set PIN', KeyRound, needPin)}
            </div>
          </section>

          {!w.has_pin && (
            <button onClick={needPin} className="w-full text-left rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-700 shrink-0" />
              <span className="flex-1"><span className="block font-semibold text-amber-900">Set your transaction PIN</span><span className="block text-sm text-amber-800">You need it before you can pay fees or send money.</span></span>
              <span className="text-sm font-bold text-amber-900">Set PIN →</span>
            </button>
          )}

          <section className="bg-white rounded-2xl border border-slate-200">
            <div className="flex gap-4 px-4 pt-3 border-b border-slate-100 text-sm">
              {([['all', 'All activity'], ['transfers', 'Transfers out']] as const).map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} className={`pb-2.5 -mb-px border-b-2 font-semibold ${tab === k ? 'border-[#0A2540] text-[#0A2540]' : 'border-transparent text-slate-500'}`}>{l}</button>
              ))}
            </div>
            {tab === 'all' ? (
              txns.isLoading ? <p className="p-4 text-sm text-slate-500">Loading…</p> : !txns.data?.length ? <p className="p-6 text-center text-sm text-slate-500">No activity yet. Top up by transferring to your account number above.</p> : (
                <ul className="divide-y divide-slate-100">
                  {txns.data.map((t) => {
                    const credit = t.type === 'credit';
                    return (
                      <li key={t.id} className="px-4 py-3 flex items-center gap-3">
                        <span className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${credit ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{credit ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{t.narration ?? t.category.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-slate-500">{fmtDate(t.created_at)} · <span className={STATUS[t.status] ?? ''}>{t.status}</span></p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`text-sm font-bold ${credit ? 'text-green-700' : 'text-slate-900'}`}>{credit ? '+' : '−'}{naira(t.amount)}</p>
                          <p className="text-[11px] text-slate-400">Bal {naira(t.balance_after)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )
            ) : (
              transfers.isLoading ? <p className="p-4 text-sm text-slate-500">Loading…</p> : !transfers.data?.length ? <p className="p-6 text-center text-sm text-slate-500">You haven't sent money to a bank yet.</p> : (
                <ul className="divide-y divide-slate-100">
                  {transfers.data.map((t) => (
                    <li key={t.id} className="px-4 py-3 flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full grid place-items-center shrink-0 bg-slate-100 text-slate-600"><Building2 className="w-4 h-4" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{t.account_name}</p>
                        <p className="text-xs text-slate-500 truncate">{t.bank_name} · {t.account_number} · {fmtDate(t.created_at)}</p>
                        {t.failure_reason && <p className="text-xs text-red-600">{t.failure_reason}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold">{naira(t.amount)}</p>
                        <p className={`text-[11px] ${STATUS[t.status] ?? 'text-slate-500'}`}>{t.status}{Number(t.fee) > 0 ? ` · fee ${naira(t.fee)}` : ''}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )
            )}
          </section>
        </>
      )}

      {send && (
        <SendDialog mode={send} balance={balance} onClose={() => setSend(null)}
          onDone={() => { setSend(null); reload(); }}
          onNeedWallet={() => { setSend(null); reload(); }}
          onNeedPin={() => { setSend(null); setPinOpen(true); }} />
      )}
      {pinOpen && <PinManager hasPin={!!w.has_pin} onClose={() => setPinOpen(false)} onDone={(m) => { setPinOpen(false); setNotice(m); reload(); }} />}
    </div>
  );
}
